/**
 * EnemyForcesPanel - enemy forces by sector and reinforcements by day (2026-10-22, Dr. Tatar).
 *
 * The enemy is entered as UNITS (battalion, regiment, brigade, division, from the rounded Soviet-model table) in a SECTOR of the front, on a DAY:
 * day 1 = the enemy at the start, day 2 or later = a reinforcement that arrives on that day. Only the SIZE is entered here; the quality
 * (training, equipment, morale) is the one of the enemy card above and is never repeated. See calculationEngine/modules/enemyForces.js for the
 * rule that keeps the ratio from being counted twice.
 */
import React, { useMemo, useState } from 'react';
import {
  Card, CardContent, Box, Typography, Alert, AlertTitle, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip, FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import { useTranslation } from 'react-i18next';
import { ENEMY_UNIT_TABLE, ENEMY_MANNING_PRESETS, enemySizeOfEntry, brigadeEnemyRatio } from '../calculationEngine/modules/enemyForces';

const fmt = (n) => Math.round(n).toLocaleString('en-US');

const EnemyForcesPanel = ({ forces, onChange, sectors, brigades, duration, cardRatio }) => {
  const { t } = useTranslation();
  const entries = forces?.entries || [];
  const defaultManning = forces?.default_manning ?? 1;
  const [draft, setDraft] = useState({ sector: '', from_km: '', to_km: '', unit: 'MR_REGIMENT', count: 1, manning: '', from_day: 1 });

  const divisions = useMemo(() => {
    const seen = new Map();
    (sectors || []).forEach((s) => { if (s.division_id && !seen.has(s.division_id)) seen.set(s.division_id, s.name.split(' / ')[0]); });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [sectors]);

  const sectorLabel = (e) => {
    if (e.sector === 'KM') return `${t('tab2.enemy_forces.km_range')} ${e.from_km}-${e.to_km} km`;
    if (e.sector === 'ALL') return t('tab2.enemy_forces.whole_front');
    if (typeof e.sector === 'string' && e.sector.startsWith('DIV:')) return `${divisions.find((d) => d.id === e.sector.slice(4))?.name || e.sector.slice(4)} (${t('tab2.enemy_forces.all_sectors')})`;
    const s = (sectors || []).find((x) => x.id === e.sector);
    return s ? `${s.name} (${s.start_km}-${s.end_km} km, ${s.brigade_name})` : e.sector;
  };

  const set = (next) => onChange({ default_manning: defaultManning, entries, ...next });
  const valid = draft.sector && Number(draft.count) > 0 && Number(draft.from_day) >= 1 && Number(draft.from_day) <= (duration || 999)
    && (draft.sector !== 'KM' || (draft.from_km !== '' && draft.to_km !== '' && Number(draft.to_km) > Number(draft.from_km)));
  const add = () => {
    if (!valid) return;
    const e = {
      id: `ef_${Date.now()}_${entries.length}`,
      sector: draft.sector,
      ...(draft.sector === 'KM' ? { from_km: Number(draft.from_km), to_km: Number(draft.to_km) } : {}),
      unit: draft.unit,
      count: Number(draft.count),
      ...(draft.manning !== '' ? { manning: Number(draft.manning) / 100 } : {}),
      from_day: Number(draft.from_day)
    };
    set({ entries: [...entries, e] });
  };

  // what each brigade faces: day 1 and each reinforcement day (the ratio the enemy card turns into a power index)
  const forcesForPreview = { default_manning: defaultManning, entries, sectors: sectors || [], brigade_frontage: Object.fromEntries((sectors || []).map((s) => [s.brigade_id, { start_km: s.start_km, end_km: s.end_km }])) };
  const previewDays = [...new Set([1, ...entries.map((e) => Number(e.from_day) || 1)])].sort((a, b) => a - b);
  const preview = (brigades || []).filter((b) => (sectors || []).some((s) => s.brigade_id === b.id)).map((b) => ({
    b,
    cells: previewDays.map((d) => {
      try {
        const r = brigadeEnemyRatio({ cardRatio, forces: forcesForPreview, brigadeId: b.id, day: d, friendlyStrength: b.strength });
        return { d, ratio: r.ratio ?? (typeof cardRatio === 'number' ? cardRatio : 1), source: r.ratio === undefined ? 'CARD_RATIO' : r.source, enemy: r.initial + r.reinforcement };
      } catch (err) { return { d, error: err.message }; }
    })
  }));

  return (
    <Card sx={{ mb: 3, border: '2px solid', borderColor: 'error.light' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <GroupsIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>{t('tab2.enemy_forces.title')}</Typography>
          {entries.length > 0 && <Chip size="small" label={`${entries.length} ${t('tab2.enemy_forces.entries')}`} />}
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab2.enemy_forces.how_title')}</AlertTitle>
          {t('tab2.enemy_forces.how')}
        </Alert>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab2.enemy_forces.default_manning')}</InputLabel>
              <Select value={Object.values(ENEMY_MANNING_PRESETS).includes(defaultManning) ? defaultManning : ''} label={t('tab2.enemy_forces.default_manning')}
                onChange={(e) => set({ default_manning: Number(e.target.value) })}>
                {Object.entries(ENEMY_MANNING_PRESETS).map(([k, v]) => (
                  <MenuItem key={k} value={v}>{t(`tab2.enemy_forces.manning_${k.toLowerCase()}`)} ({Math.round(v * 100)}%)</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('tab2.enemy_forces.default_manning_help')}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab2.enemy_forces.sector')}</InputLabel>
              <Select value={draft.sector} label={t('tab2.enemy_forces.sector')} onChange={(e) => setDraft((d) => ({ ...d, sector: e.target.value }))}>
                {(sectors || []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name} · {s.start_km}-{s.end_km} km · {s.brigade_name}</MenuItem>
                ))}
                {divisions.map((d) => (<MenuItem key={d.id} value={`DIV:${d.id}`}>{d.name} ({t('tab2.enemy_forces.all_sectors')})</MenuItem>))}
                <MenuItem value="ALL">{t('tab2.enemy_forces.whole_front')}</MenuItem>
                <MenuItem value="KM">{t('tab2.enemy_forces.km_range')}…</MenuItem>
              </Select>
              {(sectors || []).length === 0 && <FormHelperText error>{t('tab2.enemy_forces.no_sectors')}</FormHelperText>}
            </FormControl>
          </Grid>
          {draft.sector === 'KM' && (
            <>
              <Grid size={{ xs: 6, md: 1.5 }}><TextField size="small" type="number" label={t('tab2.enemy_forces.from_km')} value={draft.from_km} onChange={(e) => setDraft((d) => ({ ...d, from_km: e.target.value }))} /></Grid>
              <Grid size={{ xs: 6, md: 1.5 }}><TextField size="small" type="number" label={t('tab2.enemy_forces.to_km')} value={draft.to_km} onChange={(e) => setDraft((d) => ({ ...d, to_km: e.target.value }))} /></Grid>
            </>
          )}
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab2.enemy_forces.unit')}</InputLabel>
              <Select value={draft.unit} label={t('tab2.enemy_forces.unit')} onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))}>
                {Object.entries(ENEMY_UNIT_TABLE).map(([k, u]) => (<MenuItem key={k} value={k}>{u.label} · {fmt(u.design)}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 4, md: 1 }}><TextField size="small" type="number" label={t('tab2.enemy_forces.count')} value={draft.count} inputProps={{ min: 1, step: 1 }} onChange={(e) => setDraft((d) => ({ ...d, count: e.target.value }))} /></Grid>
          <Grid size={{ xs: 4, md: 1.2 }}><TextField size="small" type="number" label={t('tab2.enemy_forces.manning')} placeholder={`${Math.round(defaultManning * 100)}`} value={draft.manning} inputProps={{ min: 5, max: 150, step: 5 }} onChange={(e) => setDraft((d) => ({ ...d, manning: e.target.value }))} /></Grid>
          <Grid size={{ xs: 4, md: 1.3 }}><TextField size="small" type="number" label={t('tab2.enemy_forces.from_day')} value={draft.from_day} inputProps={{ min: 1, max: duration || 999, step: 1 }} onChange={(e) => setDraft((d) => ({ ...d, from_day: e.target.value }))} /></Grid>
          <Grid size={{ xs: 12, md: 'auto' }}>
            <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={!valid} onClick={add}>{t('tab2.enemy_forces.add')}</Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>{t('tab2.enemy_forces.day_help')}</Typography>

        {entries.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('tab2.enemy_forces.sector')}</TableCell>
                <TableCell>{t('tab2.enemy_forces.unit')}</TableCell>
                <TableCell align="right">{t('tab2.enemy_forces.count')}</TableCell>
                <TableCell align="right">{t('tab2.enemy_forces.manning')}</TableCell>
                <TableCell align="right">{t('tab2.enemy_forces.size')}</TableCell>
                <TableCell>{t('tab2.enemy_forces.when')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => {
                let size = null;
                try { size = enemySizeOfEntry(e, defaultManning); } catch (_err) { size = null; }
                const u = ENEMY_UNIT_TABLE[e.unit];
                return (
                  <TableRow key={e.id}>
                    <TableCell>{sectorLabel(e)}</TableCell>
                    <TableCell><Tooltip title={`${u?.exact} · ${u?.source}`}><span>{u?.label}</span></Tooltip></TableCell>
                    <TableCell align="right">{e.count}</TableCell>
                    <TableCell align="right">{Math.round((e.manning ?? defaultManning) * 100)}%</TableCell>
                    <TableCell align="right">{size === null ? '-' : fmt(size)}</TableCell>
                    <TableCell>{Number(e.from_day) > 1 ? <Chip size="small" color="warning" label={`${t('tab2.enemy_forces.reinforcement')} D+${e.from_day}`} /> : <Chip size="small" label={t('tab2.enemy_forces.initial')} />}</TableCell>
                    <TableCell align="right"><IconButton size="small" onClick={() => set({ entries: entries.filter((x) => x.id !== e.id) })}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {entries.length > 0 && preview.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('tab2.enemy_forces.preview_title')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tab2.enemy_forces.brigade')}</TableCell>
                  {previewDays.map((d) => (<TableCell key={d} align="right">D+{d}</TableCell>))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map(({ b, cells }) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.name} <Typography component="span" variant="caption" color="text.secondary">({fmt(b.strength)})</Typography></TableCell>
                    {cells.map((c) => (
                      <TableCell key={c.d} align="right">
                        {c.error ? '-' : (
                          <Tooltip title={t(`tab2.enemy_forces.source_${String(c.source).toLowerCase().replace(/\+/g, '_')}`, { defaultValue: c.source })}>
                            <span>{c.ratio.toFixed(2)}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">{t('tab2.enemy_forces.preview_help')}</Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EnemyForcesPanel;
